/**
 * DataGrid — the standard list screen (OneInvoice-style).
 *
 *  ┌ Title ─────────────────────────────── Total (N) ▾  ▽ Advanced search ┐
 *  │ [⊞ cols] [↻] [Search loaded rows…] [+ New] [Import] [Export] │ View Edit Print │
 *  │ ☐ | S.No | Col ▾ | Col ▾ | …                                           │
 *  │ ☐ | 1    | …   (row tinted by status, selected rows highlighted)       │
 *  │ Showing 1–50 of N                                        ‹ 1 / 3 ›     │
 *  └────────────────────────────────────────────────────────────────────────┘
 *
 * Props
 *  title              string | node — panel heading
 *  rows               array
 *  rowKey             string | (row) => id            default 'id'
 *  columns            [{ key, header, accessor?, render?, sortable?, width?, align?, hidden?, className?,
 *                       mobilePrimary?, mobileHidden? }]
 *                     accessor(row) → primitive used for search + sort (defaults to row[key])
 *                     render(row, ctx) → cell node (defaults to accessor value)
 *                     mobilePrimary  → this column is the card heading on a phone
 *                     mobileHidden   → leave this column off the phone card
 *
 * Below `lg` the table is replaced by one card per row (see the card block below): a
 * heading, the other columns labelled underneath, and the row actions within thumb reach.
 *  loading            bool — shows spinner on refresh button + overlay text
 *  onRefresh          () => void
 *  searchable         bool (default true) — "Search loaded rows" client-side filter
 *  searchKeys         extra row fields to include in search
 *  primaryActions     node — New / Upload / Import / Export buttons
 *  selectionActions   [{ key, label, icon, onClick(rows), requires: 'one'|'any'|'none', variant, hidden, disabled: bool | (rows) => bool }]
 *                     disabled until the selection satisfies `requires`
 *  rowActions         (row) => node — optional trailing per-row cell
 *  rowTone            (row) => 'success'|'danger'|'warning'|'info'|'muted'|null — row tint
 *  onRowDoubleClick   (row) => void
 *  advancedSearch     node — filter panel toggled by the "Advanced search" button
 *  activeFilterCount  number — badge on the Advanced search button
 *  headerExtra        node — rendered right of the title (e.g. view switcher)
 *  footerExtra        node
 *  emptyMessage       string
 *  pageSizeOptions    [25,50,100,250]; defaultPageSize 50; pagination=false disables paging
 *  dense              bool — tighter rows
 *  selectable         bool (default true)
 *  className          wrapper classes
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import ActionBar from '../shell/ActionBar';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Columns, ArrowsClockwise, CaretDown, CaretUp, CaretUpDown, CaretLeft, CaretRight,
} from '@phosphor-icons/react';

const TONE_ROW_CLASS = {
  success: 'grid-row-success',
  danger: 'grid-row-danger',
  warning: 'grid-row-warning',
  info: 'grid-row-info',
  muted: 'grid-row-muted',
};

function getKey(row, rowKey) {
  return typeof rowKey === 'function' ? rowKey(row) : row?.[rowKey];
}

function colValue(row, col) {
  if (col.accessor) return col.accessor(row);
  return row?.[col.key];
}

function toSearchText(v) {
  if (v == null) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function compare(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? -1 : 1;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export default function DataGrid({
  title,
  rows = [],
  rowKey = 'id',
  columns = [],
  loading = false,
  onRefresh,
  searchable = true,
  searchKeys = [],
  searchPlaceholder = 'Search loaded rows',
  primaryActions = null,
  selectionActions = [],
  rowActions,
  rowTone,
  onRowDoubleClick,
  advancedSearch = null,
  activeFilterCount = 0,
  headerExtra = null,
  footerExtra = null,
  emptyMessage = 'No records found',
  pageSizeOptions = [25, 50, 100, 250],
  defaultPageSize = 50,
  pagination = true,
  dense = false,
  selectable = true,
  className = '',
  'data-testid': testId = 'data-grid',
}) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(null); // {key, dir}
  const [hiddenCols, setHiddenCols] = useState(() => new Set(columns.filter((c) => c.hidden).map((c) => c.key)));
  const [selected, setSelected] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const visibleColumns = useMemo(() => columns.filter((c) => !hiddenCols.has(c.key)), [columns, hiddenCols]);

  // What a card shows on a phone: one column as the heading, the rest as labelled lines.
  // A screen can say which is which with `mobilePrimary` / `mobileHidden`; left alone, the
  // first visible column is the heading, which is what a list is usually sorted by anyway.
  const titleColumn = useMemo(
    () => visibleColumns.find((c) => c.mobilePrimary) || visibleColumns.find((c) => !c.mobileHidden) || null,
    [visibleColumns],
  );
  const metaColumns = useMemo(
    () => visibleColumns.filter((c) => c !== titleColumn && !c.mobileHidden),
    [visibleColumns, titleColumn],
  );

  // ── filter
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const parts = visibleColumns.map((c) => toSearchText(colValue(row, c)));
      searchKeys.forEach((k) => parts.push(toSearchText(row?.[k])));
      return parts.join(' ').toLowerCase().includes(q);
    });
  }, [rows, query, visibleColumns, searchKeys]);

  // ── sort
  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => compare(colValue(a, col), colValue(b, col)) * (sort.dir === 'asc' ? 1 : -1));
    return arr;
  }, [filtered, sort, columns]);

  // ── page
  const total = sorted.length;
  const pageCount = pagination ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const safePage = Math.min(page, pageCount);
  const pageRows = pagination ? sorted.slice((safePage - 1) * pageSize, safePage * pageSize) : sorted;
  const firstIndex = pagination ? (safePage - 1) * pageSize : 0;

  useEffect(() => { setPage(1); }, [query, pageSize, rows.length]);

  // drop selections for rows that disappeared
  useEffect(() => {
    const keys = new Set(rows.map((r) => getKey(r, rowKey)));
    setSelected((prev) => {
      const next = new Set([...prev].filter((k) => keys.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows, rowKey]);

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(getKey(r, rowKey))), [rows, selected, rowKey]);

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setSort((s) => {
      if (!s || s.key !== col.key) return { key: col.key, dir: 'asc' };
      if (s.dir === 'asc') return { key: col.key, dir: 'desc' };
      return null;
    });
  };

  const toggleRow = (row, additive) => {
    const k = getKey(row, rowKey);
    setSelected((prev) => {
      const next = new Set(additive ? prev : []);
      if (prev.has(k) && (additive || prev.size === 1)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const pageKeys = pageRows.map((r) => getKey(r, rowKey));
  const allPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));
  const somePageSelected = pageKeys.some((k) => selected.has(k));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageKeys.forEach((k) => next.delete(k)); else pageKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const actionEnabled = (a) => {
    if (a.requires === 'none') return true;
    if (a.requires === 'any') return selectedRows.length >= 1;
    return selectedRows.length === 1; // 'one' (default)
  };

  const rowHeight = dense ? 'h-9' : 'h-11';
  const colSpan = visibleColumns.length + (selectable ? 1 : 0) + 1 + (rowActions ? 1 : 0);

  return (
    <div className={`bg-white border border-border rounded-lg shadow-sm flex flex-col min-h-0 ${className}`} data-testid={testId}>
      <ActionBar
        bordered={false}
        title={title}
        rightSlot={(
          <>
            {headerExtra}
            {pagination ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className="h-8 bg-shell-primary hover:bg-shell-primary/90" data-testid="grid-total">
                    Total ({total}) <CaretDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel className="text-xs">Rows per page</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {pageSizeOptions.map((n) => (
                    <DropdownMenuItem key={n} onClick={() => setPageSize(n)} className={n === pageSize ? 'font-semibold' : ''}>
                      {n}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <span className="h-8 inline-flex items-center px-3 rounded-md bg-shell-primary text-white text-xs font-semibold" data-testid="grid-total">
                Total ({total})
              </span>
            )}
          </>
        )}
        advancedSearch={advancedSearch}
        activeFilterCount={activeFilterCount}
        advancedOpen={showAdvanced}
        onAdvancedToggle={setShowAdvanced}
        tools={[
          {
            key: 'columns',
            node: (
              <Popover key="columns">
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9 bg-shell-primary text-white border-shell-primary hover:bg-shell-primary/90 hover:text-white" title="Columns" data-testid="grid-columns">
                    <Columns className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-56 p-2">
                  <p className="text-xs font-semibold text-muted-foreground px-2 pb-1">Visible columns</p>
                  {columns.map((c) => (
                    <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted text-sm cursor-pointer">
                      <Checkbox
                        checked={!hiddenCols.has(c.key)}
                        onCheckedChange={(v) => setHiddenCols((prev) => {
                          const next = new Set(prev);
                          if (v) next.delete(c.key); else next.add(c.key);
                          return next;
                        })}
                      />
                      <span>{typeof c.header === 'string' ? c.header : c.key}</span>
                    </label>
                  ))}
                </PopoverContent>
              </Popover>
            ),
          },
          onRefresh && { key: 'refresh', icon: ArrowsClockwise, title: 'Refresh', onClick: onRefresh, disabled: loading, spin: loading, testid: 'grid-refresh' },
        ].filter(Boolean)}
        search={searchable ? { value: query, onChange: setQuery, placeholder: searchPlaceholder } : null}
        primary={primaryActions ? [{ key: 'primary', node: <React.Fragment key="primary">{primaryActions}</React.Fragment> }] : []}
        secondary={selectionActions.filter((a) => !a.hidden).map((a) => ({
          key: a.key,
          label: a.label,
          icon: a.icon,
          variant: a.variant || 'outline',
          disabled: !actionEnabled(a) || (typeof a.disabled === 'function' ? a.disabled(selectedRows) : a.disabled),
          onClick: () => a.onClick(selectedRows),
          testid: `grid-action-${a.key}`,
        }))}
        trailing={selectedRows.length > 0 && (
          <span data-testid="grid-selection-count">
            {selectedRows.length} selected
            <button type="button" className="ml-2 underline hover:text-foreground" onClick={() => setSelected(new Set())}>clear</button>
          </span>
        )}
      />

      {/* ── Phone: a card per row ──────────────────────────────────────────────
        * A table on a 400px screen is a table you drag sideways, and the thing you
        * actually came for — Continue, Open, the status — is the column furthest from
        * your thumb. So below `lg` each row is a card: the first column is the heading,
        * the rest are labelled underneath, and the row's actions sit at the bottom
        * where they can be tapped. Tapping the card opens the record.
        */}
      <ul className="lg:hidden border-t border-border divide-y divide-border" data-testid="grid-cards">
        {pageRows.length === 0 ? (
          <li className="py-10 text-center text-sm text-muted-foreground">{loading ? 'Loading…' : emptyMessage}</li>
        ) : pageRows.map((row, i) => {
          const k = getKey(row, rowKey);
          const isSelected = selected.has(k);
          const tone = rowTone ? rowTone(row) : null;
          const ctx = { index: firstIndex + i, selected: isSelected };
          const cell = (c) => (c.render ? c.render(row, ctx) : toSearchText(colValue(row, c)));
          const hasValue = (c) => {
            if (c.render) return true;
            const v = colValue(row, c);
            return v !== null && v !== undefined && v !== '';
          };
          return (
            <li
              key={k ?? i}
              className={`px-3 py-2.5 ${isSelected ? 'grid-row-selected' : (TONE_ROW_CLASS[tone] || '')}`}
              data-testid={`grid-card-${k}`}
            >
              <div className="flex items-start gap-2">
                {selectable && (
                  <span className="pt-1" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={isSelected} onCheckedChange={() => toggleRow(row, true)} aria-label="Select row" />
                  </span>
                )}
                <span className="pt-0.5 text-[11px] text-muted-foreground tabular-nums w-5 flex-shrink-0">{firstIndex + i + 1}</span>
                <button
                  type="button"
                  className="flex-1 min-w-0 text-left font-medium text-[14px] leading-snug break-words"
                  onClick={() => (onRowDoubleClick ? onRowDoubleClick(row) : selectable && toggleRow(row, true))}
                  data-testid={`grid-card-open-${k}`}
                >
                  {titleColumn ? cell(titleColumn) : `Row ${firstIndex + i + 1}`}
                </button>
              </div>

              {metaColumns.filter(hasValue).length > 0 && (
                <dl className="mt-1.5 ml-7 grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-1 text-[12px]">
                  {metaColumns.filter(hasValue).map((c) => (
                    <React.Fragment key={c.key}>
                      <dt className="text-muted-foreground whitespace-nowrap">{c.header}</dt>
                      <dd className="min-w-0 break-words">{cell(c)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}

              {rowActions && (
                <div className="mt-2 ml-7 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()} data-testid={`grid-card-actions-${k}`}>
                  {rowActions(row)}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Table (wide screens) */}
      <div className="hidden lg:block overflow-auto border-t border-border">
        <table className="w-full text-sm border-collapse" data-testid="grid-table">
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr className="text-xs text-foreground/80">
              {selectable && (
                <th className="w-10 px-3 text-left border-b border-border">
                  <Checkbox
                    checked={allPageSelected ? true : somePageSelected ? 'indeterminate' : false}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                    data-testid="grid-select-all"
                  />
                </th>
              )}
              <th className="w-14 px-3 h-10 text-left font-semibold border-b border-border">S.No</th>
              {visibleColumns.map((c) => {
                const isSorted = sort?.key === c.key;
                const sortable = c.sortable !== false;
                return (
                  <th
                    key={c.key}
                    style={c.width ? { width: c.width, minWidth: c.width } : undefined}
                    className={`px-3 h-10 font-semibold border-b border-border border-l border-l-border/60 whitespace-nowrap ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'} ${sortable ? 'cursor-pointer select-none hover:bg-muted' : ''} ${c.headerClassName || ''}`}
                    onClick={() => toggleSort(c)}
                    data-testid={`grid-col-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.header}
                      {sortable && (isSorted
                        ? (sort.dir === 'asc' ? <CaretUp className="w-3 h-3" /> : <CaretDown className="w-3 h-3" />)
                        : <CaretUpDown className="w-3 h-3 text-muted-foreground/60" />)}
                    </span>
                  </th>
                );
              })}
              {rowActions && <th className="px-3 h-10 text-right font-semibold border-b border-border border-l border-l-border/60 w-px whitespace-nowrap">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="py-12 text-center text-muted-foreground">
                  {loading ? 'Loading…' : emptyMessage}
                </td>
              </tr>
            ) : pageRows.map((row, i) => {
              const k = getKey(row, rowKey);
              const isSelected = selected.has(k);
              const tone = rowTone ? rowTone(row) : null;
              return (
                <tr
                  key={k ?? i}
                  className={`${rowHeight} border-b border-border/70 transition-colors cursor-default ${isSelected ? 'grid-row-selected' : (TONE_ROW_CLASS[tone] || 'hover:bg-muted/40')}`}
                  onClick={(e) => { if (selectable) toggleRow(row, e.ctrlKey || e.metaKey || e.shiftKey); }}
                  onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row)}
                  aria-selected={isSelected}
                  data-testid={`grid-row-${k}`}
                >
                  {selectable && (
                    <td className="px-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleRow(row, true)} aria-label="Select row" />
                    </td>
                  )}
                  <td className="px-3 text-muted-foreground tabular-nums">{firstIndex + i + 1}</td>
                  {visibleColumns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''} ${c.className || ''}`}
                    >
                      {c.render ? c.render(row, { index: firstIndex + i, selected: isSelected }) : toSearchText(colValue(row, c))}
                    </td>
                  ))}
                  {rowActions && (
                    <td className="px-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {rowActions(row)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {(pagination || footerExtra) && (
        <div className="flex items-center gap-3 px-4 py-2 border-t border-border text-xs text-muted-foreground">
          {footerExtra}
          {pagination && (
            <>
              <span className="mr-auto" data-testid="grid-range">
                {total === 0 ? 'No rows' : `Showing ${firstIndex + 1}–${Math.min(firstIndex + pageSize, total)} of ${total}`}
                {query && ` (filtered from ${rows.length})`}
              </span>
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={safePage <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                <CaretLeft className="w-4 h-4" />
              </Button>
              <span className="tabular-nums">{safePage} / {pageCount}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={safePage >= pageCount} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                <CaretRight className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export { default as StatusBadge, toneFor } from './StatusBadge';
